import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { peopleKeys } from '../people/keys.ts'
import { sessionKeys } from './keys.ts'

/**
 * "My profile" — the directory record belonging to the signed-in user.
 *
 * A separate hook from `usePerson` on purpose: the endpoint behind it is
 * authorized by ownership rather than permission, and a member has no
 * `people:write` to call the staff one with.
 */

const myProfileKey = () => [...sessionKeys.all(), 'my-person'] as const

export function useMyProfile() {
  const api = useApi()
  return useQuery({
    queryKey: myProfileKey(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.me.person.get()
      if (error) {
        // A login with no linked directory record is a normal state, not a
        // failure — the screen says so instead of showing an error.
        if (error.status === 404) return null
        throw error
      }
      return data
    },
  })
}

export type MyProfile = NonNullable<NonNullable<ReturnType<typeof useMyProfile>['data']>>

function useProfileInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: myProfileKey() })
    // Staff screens show the same person.
    void queryClient.invalidateQueries({ queryKey: peopleKeys.all() })
  }
}

export function useUpdateMyProfile() {
  const api = useApi()
  const invalidate = useProfileInvalidation()
  return useMutation({
    mutationFn: async (body: Parameters<typeof api.api.v1.me.person.patch>[0]) => {
      const { data, error } = await api.api.v1.me.person.patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

/** Same three steps as the staff photo upload, against the self endpoints. */
export function useUploadMyPhoto() {
  const api = useApi()
  const invalidate = useProfileInvalidation()
  return useMutation({
    mutationFn: async (file: { type: 'image/jpeg' | 'image/png' | 'image/webp'; body: Blob }) => {
      const { data, error } = await api.api.v1.me.person.photo['upload-url'].post({
        contentType: file.type,
        sizeBytes: file.body.size,
      })
      if (error) throw error

      const uploaded = await fetch(data.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file.body,
      })
      if (!uploaded.ok) throw new Error(`Storage rejected the upload (${uploaded.status}).`)

      const confirmed = await api.api.v1.me.person.photo.put({ key: data.key })
      if (confirmed.error) throw confirmed.error
      return confirmed.data
    },
    onSuccess: invalidate,
  })
}

export function useRemoveMyPhoto() {
  const api = useApi()
  const invalidate = useProfileInvalidation()
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1.me.person.photo.delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })
}
